import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AIChat from './AIChat';
import { BIGQUERY_CONFIG, API_BASE_URL } from '../config';
import './Header.css';

// ACO Insights Logo Component
const ACOInsightsLogo = () => (
  <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Gradient definitions */}
    <defs>
      <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#60a5fa" stopOpacity="1" />
        <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    
    {/* Modern chart bars with light blue gradient */}
    <rect x="10" y="26" width="4" height="9" rx="2" fill="url(#barGradient)"/>
    <rect x="16" y="22" width="4" height="13" rx="2" fill="url(#barGradient)"/>
    <rect x="22" y="24" width="4" height="11" rx="2" fill="url(#barGradient)"/>
    <rect x="28" y="19" width="4" height="16" rx="2" fill="url(#barGradient)"/>
    <rect x="34" y="21" width="4" height="14" rx="2" fill="url(#barGradient)"/>
    
    {/* Modern pulse/heartbeat line in green */}
    <path d="M 8 12 L 12 12 L 14 9 L 16 15 L 18 11 L 20 12 L 40 12" 
          stroke="#10b981" 
          strokeWidth="2" 
          fill="none" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          opacity="0.95"/>
    
    {/* Pulse dot for animation effect in green */}
    <circle cx="14" cy="9" r="1.5" fill="#10b981" opacity="0.8"/>
  </svg>
);

// SDCorner Logo Component (subtle)
const SDCornerLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Rounded square */}
    <rect x="3" y="3" width="18" height="18" rx="3" fill="none" stroke="#64748b" strokeWidth="1.5"/>
    
    {/* Diagonal lines representing corner */}
    <line x1="8" y1="8" x2="16" y2="16" stroke="#64748b" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="16" y1="8" x2="8" y2="16" stroke="#94a3b8" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
  </svg>
);

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isHomePage = location.pathname === '/';
  const [chatOpen, setChatOpen] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/landing');
  };
  
  const handleDemoLogin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/demo-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.token) {
        localStorage.setItem('jwt_token', data.token);
        localStorage.setItem('user_info', JSON.stringify(data.user));
        localStorage.setItem('auth_version', '2'); // Required by AuthContext to keep token on reload
        window.location.reload(); // Refresh to load token
      } else {
        alert('Login failed: No token received');
      }
    } catch (error) {
      console.error('Demo login failed:', error);
      alert(`Failed to login: ${error.message}`);
    }
  };
  // Extract dataset/table from current path for AI context
  const getContextFromPath = () => {
    const pathMap = {
      'demographics': { table: 'demographics_2024', dataset: 'Dashboards' },
      'chronic': { table: 'demographics_2024', dataset: 'Dashboards' },
      'preventive': { table: 'demographics_2024', dataset: 'Dashboards' },
      'er': { table: 'ER_2024', dataset: 'Dashboards' },
      'inp': { table: 'INP_2024', dataset: 'Dashboards' },
      'snf': { table: 'SNF_2024', dataset: 'Dashboards' },
      'hha': { table: 'HHA_2024', dataset: 'Dashboards' },
      'hos': { table: 'HOS_2024', dataset: 'Dashboards' },
      'op': { table: 'OP_2024', dataset: 'Dashboards' },
      'phy': { table: 'PHY_2024', dataset: 'Dashboards' },
      'dme': { table: 'DME_2024', dataset: 'Dashboards' },
      'toc': { table: 'demographics_2024', dataset: 'Dashboards' },
    };
    
    for (const [key, value] of Object.entries(pathMap)) {
      if (location.pathname.includes(key)) {
        return value;
      }
    }
    
    return { table: 'demographics_2024', dataset: 'Dashboards' };
  };
  
  const context = getContextFromPath();
  const dashboardName = location.pathname.split('/').pop() || 'Home';
  
  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <Link to="/" className="logo-link" title="Go to Home">
            <div className="logo-group">
              <ACOInsightsLogo />
              <h1 className="app-title">ACO Insights</h1>
            </div>
          </Link>
        </div>
        <nav className="header-nav">
          <Link to="/" className="nav-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Home</span>
          </Link>
          <button 
            className="ai-chat-button"
            onClick={() => setChatOpen(true)}
            title="Open AI Data Assistant"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" fill="currentColor"/>
            </svg>
            <span>Ask AI</span>
          </button>
          <button 
            className="logout-button"
            onClick={handleLogout}
            title="Logout and return to landing page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Logout</span>
          </button>
        </nav>
      </header>
      
      <AIChat 
        isOpen={chatOpen} 
        onClose={() => setChatOpen(false)}
        dataset={context.dataset}
        table={context.table}
        project={BIGQUERY_CONFIG.PROJECT}
        context={`Current dashboard: ${dashboardName}`}
      />
    </>
  );
}

export default Header;
